"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Reports whether an element is close to the viewport and the document is
 * visible. Consumers can pause non-essential timers and animations otherwise.
 */
export function useElementActivity<T extends HTMLElement>(
  rootMargin = "200px 0px"
) {
  const ref = useRef<T>(null);
  const [isNearViewport, setIsNearViewport] = useState(false);
  const [isDocumentVisible, setIsDocumentVisible] = useState(true);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    if (!("IntersectionObserver" in window)) {
      setIsNearViewport(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsNearViewport(entry?.isIntersecting ?? false),
      { rootMargin }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [rootMargin]);

  useEffect(() => {
    const update = () => setIsDocumentVisible(!document.hidden);
    update();
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);

  return { ref, isActive: isNearViewport && isDocumentVisible };
}
